import type { Product } from '../models/ProductModels';
import type { SearchState } from '../models/SearchModels';

function normalizeDivision(productDivision: string, currentDivision: string): string {
  if (productDivision.toLowerCase() !== 'both') return productDivision.toLowerCase();
  return currentDivision.toLowerCase() === 'all' ? 'university' : currentDivision.toLowerCase();
}

export function formatPublicationDate(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

export function disciplineUrl(product: Product, state: SearchState): string {
  const division = normalizeDivision(product.Division, state.division);
  return division === 'primary'
    ? `/search/pg/1/sortOrder/r/div/primary/f/${product.DisciplineData.trim()}/qry/`
    : `/search/pg/1/sortOrder/r/div/${division}/cd/${product.DisciplineData.trim()}/dt/c/qry/`;
}

export function subjectUrl(product: Product, state: SearchState): string {
  const division = normalizeDivision(product.Division, state.division);
  return division === 'primary' || division === 'secondary'
    ? `/search/pg/1/sortOrder/r/div/${division}/f/${product.SubjectData.trim()}/qry/`
    : `/search/pg/1/sortOrder/r/div/${division}/cd/${product.DisciplineData.trim()}/d/${product.SubjectData.trim()}/dt/cd/qry/`;
}

export function seriesUrl(product: Product, state: SearchState): string {
  const division = normalizeDivision(product.Division, state.division);
  return division === 'primary' || division === 'secondary'
    ? `/search/pg/1/sortOrder/r/div/${division}/f/${product.SeriesData.trim()}/qry/`
    : `/search/pg/1/sortOrder/r/div/${division}/cd/${product.DisciplineData.trim()}/d/${product.SubjectData.trim()}/s/${product.SeriesData.trim()}/dt/cds/qry/`;
}

export function shouldRedirectSingleProduct(products: Product[], count: number, state: SearchState): boolean {
  return count === 1 && state.division !== 'all' && !state.facetSelected && !state.disciplineType && !state.showFacet
    && /^(?:\d{13}|\d{8}|\d{6})$/.test(state.query);
}

export function pageRange(current: number, total: number, maximum = 6): number[] {
  if (total <= 0) return [];
  let start = Math.max(1, current - Math.floor(maximum / 2));
  let end = Math.min(total, start + maximum - 1);
  start = Math.max(1, end - maximum + 1);
  return Array.from({ length: end - start + 1 }, (_, index) => start + index);
}

export function formatCurrency(value: number | string, countryCode: string): string {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return String(value ?? '');
  return new Intl.NumberFormat(countryCode === 'NZ' ? 'en-NZ' : 'en-AU', {
    style: 'currency', currency: countryCode === 'NZ' ? 'NZD' : 'AUD'
  }).format(amount);
}
